import { useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, GraduationCap, Briefcase } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { applyTheme, getChoice, setChoice } from "@/lib/edition";

const EDITIONS = [
  { id: "student", label: "Student", sub: "Timetable, homework, revision", icon: GraduationCap },
  { id: "corporate", label: "Corporate", sub: "Meetings, tasks, deadlines", icon: Briefcase },
];

const EditionPicker = ({ value, onChange }) => (
  <div data-testid="edition-picker" role="radiogroup" aria-label="Choose your edition" className="grid grid-cols-2 gap-2">
    {EDITIONS.map(({ id, label, sub, icon: I }) => (
      <button key={id} type="button" role="radio" aria-checked={value === id} data-testid={`edition-${id}`} onClick={() => onChange(id)}
        className={`flex flex-col items-start gap-1 rounded-2xl border p-3.5 text-left transition-colors ${value === id ? "border-gold-400 bg-gold-400/15 shadow-gold" : "border-slate-300 bg-surface hover:border-gold-400"}`}>
        <I className={`w-5 h-5 ${value === id ? "text-gold-300" : "text-slate-500"}`} />
        <span className="font-semibold text-slate-900">{label}</span>
        <span className="text-xs text-slate-600">{sub}</span>
      </button>
    ))}
  </div>
);

const HERO = {
  student: ["Your school day,", "sorted by 7am.", "Your timetable, homework and kit list, turned into a morning brief and a 19:00 packing reminder."],
  corporate: ["Your workday,", "planned by 7am.", "Your meetings, tasks and deadlines, turned into a focused morning brief with a plan for the day."],
};

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
);

export default function Login() {
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [edition, setEdition] = useState(getChoice());
  const pick = (e) => { setEdition(e); setChoice(e); applyTheme(e); };
  const hero = HERO[edition];

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { mode === "signup" ? await register(name, email, password, edition) : await login(email, password); } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main data-testid="login-page" className="relative min-h-screen grid lg:grid-cols-2">
      <section className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-navy-900 text-white p-12">
        <div className="absolute -bottom-40 -left-20 w-[620px] h-[620px] rounded-full bg-brand/80 blur-[2px] sunrise" />
        <div className="absolute bottom-0 inset-x-0 h-56 bg-gradient-to-t from-teal-500/40 to-transparent" />
        <div className="relative flex items-center gap-3"><Logo className="w-10 h-10" /><span className="font-mono text-xs uppercase tracking-[0.25em] text-slate-300">Daybreyk</span></div>
        <div className="relative">
          <h2 className="font-display text-7xl leading-[0.95]">{hero[0]}<br /><span className="italic text-gold-300">{hero[1]}</span></h2>
          <p className="mt-6 max-w-md text-slate-300">{hero[2]}</p>
        </div>
      </section>
      <section className="flex items-center justify-center px-6 py-16">
        <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className="w-full max-w-sm">
          <Logo className="w-12 h-12 lg:hidden" />
          <h1 className="mt-6 font-display text-5xl text-slate-900">{mode === "signup" ? "Create your account." : "Good morning."}</h1>
          <p className="mt-2 text-slate-600">{mode === "signup" ? "Free. Your data stays private to you." : "Sign in to read today's brief."}</p>
          <p className="mt-6 mb-2 text-xs font-mono uppercase tracking-widest text-slate-500">{mode === "signup" ? "I'm signing up as" : "Edition"}</p>
          <EditionPicker value={edition} onChange={pick} />
          <button type="button" data-testid="google-signin-button" onClick={google} className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-xl border border-slate-300 bg-surface py-3 font-semibold text-slate-800 hover:bg-slate-50 transition-colors">
            <GoogleIcon /> Continue with Google
          </button>
          <div className="my-6 flex items-center gap-3 text-xs font-mono uppercase tracking-widest text-slate-500"><span className="h-px flex-1 bg-slate-200" />or<span className="h-px flex-1 bg-slate-200" /></div>
          <div className="space-y-3">
            {mode === "signup" && <input data-testid="signup-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="w-full rounded-xl border border-slate-300 bg-surface px-4 py-3 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition" />}
            <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full rounded-xl border border-slate-300 bg-surface px-4 py-3 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition" />
            <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="w-full rounded-xl border border-slate-300 bg-surface px-4 py-3 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition" />
          </div>
          {error && <p data-testid="login-error" className="mt-3 text-sm text-rose-600">{error}</p>}
          <button data-testid="login-submit-button" disabled={busy} className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-brand py-3 font-semibold text-white hover:bg-brand-dark active:scale-[0.99] disabled:opacity-60 transition">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {mode === "signup" ? "Create account" : "Sign in"}
          </button>
          <button type="button" data-testid="toggle-auth-mode" onClick={() => { setMode(mode === "signup" ? "signin" : "signup"); setError(""); }} className="mt-4 w-full text-sm font-semibold text-teal-700 hover:underline">
            {mode === "signup" ? "Already have an account? Sign in" : "New here? Create an account"}
          </button>
          <p className="mt-6 text-xs text-slate-500">Passwords need at least 8 characters. Signing in uses the edition your account was created with.</p>
        </motion.form>
      </section>
    </main>
  );
}
